import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { StackProvider, StackTheme, StackHandler } from "@stackframe/react";
import { stackClient } from './lib/stack';
import LandingPage from './pages/LandingPage';
import SignInPage from './pages/SignInPage';
import AuthCallback from './pages/AuthCallback';
import StackAuthCallback from './pages/StackAuthCallback';
import OAuthCallback from './pages/OAuthCallback';
import OnboardingPage from './pages/OnboardingPage';
import HomePage from './pages/HomePage';
import AssessmentPage from './pages/AssessmentPage';
import ReportPage from './pages/ReportPage';
import DashboardPage from './pages/DashboardPage';
import UserDashboard from './pages/UserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import DBAnalytics from './pages/DBAnalytics';
import BiharAnalytics from './pages/BiharAnalytics';
import MigratePage from './pages/MigratePage';
import PhysicalAssessment from './components/camera/PhysicalAssessment';
import PoseTrackerTestPage from './pages/PoseTrackerTestPage';
import MediaPipePage from './pages/MediaPipePage';
import PhysicalTestsModule from './components/physical/PhysicalTestsModule';
import { AssessmentProvider } from './context/AssessmentContext';
import { AuthProvider } from './context/AuthContext';
import Header from './components/common/Header';
import StackAuthSync from './components/auth/StackAuthSync';

function HandlerRoutes() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isClient, setIsClient] = React.useState(false);
  
  // Ensure client-side only rendering to prevent hydration errors
  React.useEffect(() => {
    setIsClient(true);
  }, []);
  
  // After StackHandler processes, check user status and redirect appropriately
  React.useEffect(() => {
    if (isClient && location.pathname.includes('/handler/oauth-callback')) {
      console.log('🔐 OAuth callback detected, StackHandler will process...');
      
      // Wait for StackHandler to set cookies, then check user status
      const checkAndRedirect = async () => {
        const hasStackCookies = document.cookie.includes('stack-access');
        console.log('📊 Checking cookies:', hasStackCookies ? 'Stack cookies present' : 'No cookies yet');
        
        if (hasStackCookies) {
          console.log('✅ Stack Auth successful, checking user status...');
          
          try {
            // Get Stack Auth user
            const stackUser = await stackClient.getUser();
            
            if (stackUser && stackUser.primaryEmail) {
              console.log('👤 Stack user found:', stackUser.primaryEmail);
              
              // Check if user exists in database and has completed onboarding
              const response = await fetch('/api', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  action: 'user.get',
                  email: stackUser.primaryEmail
                })
              });
              
              const result = await response.json();
              console.log('📊 User check result:', result);
              
              if (result.success && result.user) {
                const user = result.user;
                
                // Check if onboarding completed
                if (user.onboarding_completed) {
                  console.log('✅ User already onboarded, routing by role:', user.role);
                  
                  // Route based on role
                  if (user.role === 'admin' || user.role === 'super_admin') {
                    navigate('/db-analytics', { replace: true });
                  } else {
                    navigate('/user-dashboard', { replace: true });
                  }
                } else {
                  console.log('📝 User needs onboarding');
                  navigate('/onboarding', { replace: true });
                }
              } else {
                // New user - needs onboarding
                console.log('🆕 New user, redirecting to onboarding');
                navigate('/onboarding', { replace: true });
              }
            } else {
              console.log('⚠️ No Stack user found, redirecting to onboarding');
              navigate('/onboarding', { replace: true });
            }
          } catch (error) {
            console.error('❌ Error checking user status:', error);
            // Fallback to onboarding on error
            navigate('/onboarding', { replace: true });
          }
        } else {
          // Keep checking for cookies
          setTimeout(checkAndRedirect, 500);
        }
      };
      
      // Start checking after a short delay to let StackHandler process
      setTimeout(checkAndRedirect, 1000);
    }
  }, [isClient, location, navigate]);
  
  // Only render StackHandler on client-side to avoid hydration errors
  if (!isClient) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white mx-auto mb-4" />
          <p className="text-white text-lg">Completing authentication...</p>
        </div>
      </div>
    );
  }
  
  // Render StackHandler on client-side only
  return <StackHandler />;
}

function AppContent() {
  const location = useLocation();
  const showHeader = location.pathname === '/dashboard';
  
  return (
    <>
      <StackAuthSync />
      {showHeader && <Header />}
      <Routes>
        <Route path="/handler/*" element={<HandlerRoutes />} />
        <Route path="/stack-callback" element={<StackAuthCallback />} />
        <Route path="/oauth-callback" element={<OAuthCallback />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/user-dashboard" element={<UserDashboard />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/db-analytics" element={<DBAnalytics />} />
        <Route path="/db-bihar" element={<BiharAnalytics />} />
        <Route path="/migrate" element={<MigratePage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/assessment" element={<AssessmentPage />} />
        <Route path="/physical" element={<PhysicalTestsModule />} />
        <Route path="/posetracker" element={<PoseTrackerTestPage />} />
        <Route path="/mediapipe" element={<MediaPipePage />} />
        <Route path="/report/:id" element={<ReportPage />} />
        <Route path="/report/:id/:type" element={<ReportPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <StackProvider app={stackClient}>
      <StackTheme>
        <Router>
          <AuthProvider>
            <AssessmentProvider>
              <div className="app min-h-screen">
                <AppContent />
              </div>
            </AssessmentProvider>
          </AuthProvider>
        </Router>
      </StackTheme>
    </StackProvider>
  );
}

export default App;
